/**
 * User Access Management - Compact Enterprise UI
 *
 * PRD: Compact, enterprise-grade UI with high information density
 * - Single toolbar row with Add+, Search, inline counters
 * - Segmented control for dataset switching
 * - Table-like list with hover-revealed actions
 * - Compact spacing: header 56-64px, rows 40-44px, buttons 28-32px
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';
import { UserPermissionsEditor } from './UserPermissionsEditor';
import { AnimatedModal } from '@/components/ui/AnimatedModal';
import { useToast } from '@/components/ui/Toast';
import { useConfirmDialog, ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { WaveLoader } from '@/components/ui/WaveLoader';
import {
  PartnerCompany,
  PartnerUser,
  ExternalPartnerStatus,
  listPartnerCompanies,
  listPartnerUsers,
  getExternalPartnerStatus,
  createPartnerCompany,
  createPartnerUsers,
  archivePartnerCompany,
  unarchivePartnerCompany,
  resetPartnerUserPassword,
  suspendPartnerUser,
  unsuspendPartnerUser,
  deletePartnerUser,
  syncFromGoogle,
  CreateCompanyInput,
  BulkCreateUsersInput,
} from '@/services/externalPartners';

// ============================================================================
// INTERFACES
// ============================================================================

interface User {
  id: string;
  name: string;
  email: string;
  picture?: string;
  role: 'Admin' | 'Staff';
  isActive: boolean;
  lastLoginAt?: string;
  activatedAt?: string;
  activatedBy?: string;
  createdAt?: string;
  clientAccess?: ClientAccess[];
}

interface ClientAccess {
  clientId: string;
  clientName: string;
  hasAccess: boolean;
}

interface Client {
  id: string;
  name: string;
  code?: string;
}

interface PermissionDefinition {
  id: string;
  action: string;
  name: string;
  description: string;
  adminDefault: boolean;
  staffDefault: boolean;
}

type PermissionDefinitions = Record<string, PermissionDefinition[]>;

interface NewPartnerUserRow {
  id: string;
  firstName: string;
  lastName: string;
  externalEmail?: string;
}

interface AllowedEmail {
  id: string;
  email: string;
  name: string | null;
  autoActivate: boolean;
  addedBy: string;
  addedAt: string;
  isActive: boolean;
  notes: string | null;
  hasUser: boolean;
  userActive: boolean;
  userLastLogin: string | null;
  addedByUser: { id: string; name: string; email: string };
}

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Dataset = 'internal' | 'external';
type InternalFilter = 'all' | 'active' | 'pre-invited' | 'admin' | 'staff';
type ExternalFilter = 'all' | 'active' | 'inactive';

// Unified internal person (user or pre-invited email)
interface InternalPerson {
  id: string;
  type: 'user' | 'pre-invited';
  name: string;
  email: string;
  picture?: string;
  role?: 'Admin' | 'Staff';
  isActive: boolean;
  status: 'active' | 'pending' | 'pre-invited';
  lastLoginAt?: string;
  autoActivate?: boolean;
  notes?: string;
  clientAccess?: ClientAccess[];
  // Original user object for user type
  originalUser?: User;
  // Original email object for pre-invited type
  originalEmail?: AllowedEmail;
}

// Partner company with loaded users
interface PartnerCompanyWithUsers extends PartnerCompany {
  users?: PartnerUser[];
  usersLoading?: boolean;
}

// ============================================================================
// ICON-BUTTON LABEL
// ============================================================================

/**
 * Hover/focus label for an icon-only action button in this modal.
 *
 * Every icon-only button here is opaque on its own — a key, a building, a person
 * with a slash say nothing about what clicking them does. This renders the answer
 * on hover, and the same wording goes on the button's `aria-label` so a screen
 * reader gets it too. Do not also set a native `title` on a button that renders
 * one of these: the browser tooltip would appear a second later on top of it.
 *
 * Two things keep it from disturbing the rows it sits in:
 * - It is absolutely positioned and `pointer-events-none`, so it adds no height to
 *   the 44px/40px rows and can never swallow the click meant for the button.
 * - Placement is declared, not measured. The modal panel and the list body both
 *   clip their overflow, so `top` is only used where a row or the sticky table
 *   header sits above the button, and `bottom` where panel content sits below it.
 *   A label pointed at a clipped edge would simply be cut in half.
 *
 * The parent button must carry `relative group/tip` (or be positioned already, in
 * which case `group/tip` alone is enough) for the anchor and the hover to work.
 */
const ActionLabel: React.FC<{
  text: string;
  placement?: 'top' | 'bottom';
  align?: 'left' | 'right';
}> = ({ text, placement = 'top', align = 'right' }) => (
  <span
    aria-hidden="true"
    className={`pointer-events-none absolute z-20 hidden whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs font-medium leading-none text-white shadow-lg group-hover/tip:block group-focus-visible/tip:block ${
      placement === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
    } ${align === 'right' ? 'right-0' : 'left-0'}`}
  >
    {text}
  </span>
);

// ============================================================================
// ROW ACTION CLUSTER
// ============================================================================

/**
 * Classes for the hover-revealed action cluster at the right edge of every row.
 *
 * These used to sit at `opacity-0` until the row was hovered, which meant a row
 * advertised nothing at all: there was no way to know an employee could be given
 * client access, or a partner user suspended, without sweeping the pointer across
 * the table. They now rest faintly visible and come to full strength on hover, so
 * the affordance is announced without the row reading as busy.
 *
 * `focus-within` matters as much as `hover` here — tabbing into a cluster that is
 * painted at zero opacity moves focus somewhere invisible.
 *
 * `um-row-actions` carries no styles. It is a stable, minification-proof marker
 * for grepping a built bundle to confirm this change actually shipped.
 */
const ROW_ACTIONS_CLASS =
  'um-row-actions flex justify-end gap-1 opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100';

/**
 * Inset accent bars marking the row that owns an open panel.
 *
 * An inset box-shadow rather than a border: a real left border would shift the
 * row's contents by its own width and knock the columns out of alignment with
 * every other row (the same reason `DepositsTable` draws its status accents this
 * way). Blue tracks client access, purple tracks permissions — the colours the
 * two trigger buttons already turn when active.
 */
const OPEN_PANEL_ACCENT_BLUE = 'shadow-[inset_2px_0_0_0_#60a5fa]';
const OPEN_PANEL_ACCENT_PURPLE = 'shadow-[inset_2px_0_0_0_#c084fc]';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const UserManagementModal: React.FC<UserManagementModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialogProps } = useConfirmDialog();

  // Core state
  const [activeDataset, setActiveDataset] = useState<Dataset>('internal');
  const [searchTerm, setSearchTerm] = useState('');
  const [addDropdownOpen, setAddDropdownOpen] = useState(false);
  const addDropdownRef = useRef<HTMLDivElement>(null);

  // Dataset-specific filters
  const [internalFilter, setInternalFilter] = useState<InternalFilter>('all');
  const [externalFilter, setExternalFilter] = useState<ExternalFilter>('all');

  // External partner state - Company Hierarchy
  const [showAddCompanyForm, setShowAddCompanyForm] = useState(false);
  const [showAddPartnerUserForm, setShowAddPartnerUserForm] = useState<string | null>(null); // partnerId when adding user
  const [expandedCompanyId, setExpandedCompanyId] = useState<string | null>(null);
  const [newCompany, setNewCompany] = useState<CreateCompanyInput>({ name: '', description: '', primaryContact: '' });
  const [newPartnerUsers, setNewPartnerUsers] = useState<NewPartnerUserRow[]>([
    { id: 'row-1', firstName: '', lastName: '', externalEmail: '' },
  ]);
  const [partnerUserRole, setPartnerUserRole] = useState<'Admin' | 'Staff'>('Staff');
  const [partnerClientIds, setPartnerClientIds] = useState<string[]>([]);
  const [partnerPermissionOverrides, setPartnerPermissionOverrides] = useState<Map<string, boolean>>(new Map());
  const [partnerPermissionsOpen, setPartnerPermissionsOpen] = useState(false);
  const [expandedPermissionCategories, setExpandedPermissionCategories] = useState<Set<string>>(new Set());
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [createdCredentials, setCreatedCredentials] = useState<Array<{ email: string; credentialLink: string; credentialExpiresAt: string; externalEmail?: string }> | null>(null);
  const [resetCredentials, setResetCredentials] = useState<{ email: string; credentialLink: string; credentialExpiresAt: string; externalEmail?: string | null } | null>(null);
  const [bulkCreateErrors, setBulkCreateErrors] = useState<string[]>([]);
  const [companyUsers, setCompanyUsers] = useState<Record<string, PartnerUser[]>>({});
  const [lastSyncTime, setLastSyncTime] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // User management state
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Record<string, { clientIds?: string[] }>>({});
  const [pendingActivation, setPendingActivation] = useState<Record<string, string[]>>({});
  const [permissionsUserId, setPermissionsUserId] = useState<string | null>(null);

  // Partner user permissions editing state
  const [partnerPermissionsUser, setPartnerPermissionsUser] = useState<{ appUserId: string; name: string; role: 'Admin' | 'Staff' } | null>(null);

  // Partner user client access editing state
  const [partnerClientAccessUser, setPartnerClientAccessUser] = useState<{ appUserId: string; name: string; currentClientIds: string[] } | null>(null);
  const [partnerClientAccessPending, setPartnerClientAccessPending] = useState<string[]>([]);

  // Add forms state
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [editingEmail, setEditingEmail] = useState<AllowedEmail | null>(null);

  // Form data
  const [newEmail, setNewEmail] = useState<{
    email: string;
    name: string;
    autoActivate: boolean;
    notes: string;
    provisioningData?: {
      role?: 'Admin' | 'Staff';
      clientIds?: string[];
    };
  }>({ email: '', name: '', autoActivate: true, notes: '', provisioningData: { role: 'Staff', clientIds: [] } });
  const [addUserError, setAddUserError] = useState<string | null>(null);

  /**
   * Every open starts from a clean slate.
   *
   * `Header` keeps this component mounted for the whole session and only flips
   * `isOpen`, so nothing here is ever torn down. Without this effect the modal
   * remembered its entire previous visit: close it with someone's permissions
   * panel expanded and it reopened with that panel still expanded, on the same
   * tab, under the same filter, with the same search text and the same
   * half-finished invite form.
   *
   * It runs on the false -> true transition only, never on close. The panel plays
   * a 200ms exit animation with its content still on screen, and collapsing that
   * content mid-animation is plainly visible. Resetting on open is also free of a
   * stale-content flash: `AnimatedModal` renders nothing until its own open effect
   * fires, and that effect and this one land in the same commit, so the first
   * frame the user ever sees is already clean.
   *
   * Deliberately NOT reset: `lastSyncTime` and `isSyncing`, which hold the Google
   * partner sync to once per five minutes. They are a cooldown ledger rather than
   * view state, and clearing them would fire a fresh sync on every single open.
   */
  useEffect(() => {
    if (!isOpen) return;

    // Toolbar: tab, search, sub-filters
    setActiveDataset('internal');
    setSearchTerm('');
    setInternalFilter('all');
    setExternalFilter('all');
    setAddDropdownOpen(false);

    // Expanded rows and the panels they own
    setExpandedUserId(null);
    setPermissionsUserId(null);
    setExpandedCompanyId(null);
    setPartnerPermissionsUser(null);
    setPartnerClientAccessUser(null);
    setPartnerClientAccessPending([]);

    // Unsaved per-row edits
    setPendingChanges({});
    setPendingActivation({});
    setEditingEmail(null);

    // Add / invite forms
    setShowAddUserForm(false);
    setAddUserError(null);
    setNewEmail({ email: '', name: '', autoActivate: true, notes: '', provisioningData: { role: 'Staff', clientIds: [] } });
    setShowAddCompanyForm(false);
    setNewCompany({ name: '', description: '', primaryContact: '' });
    setShowAddPartnerUserForm(null);
    setNewPartnerUsers([{ id: 'row-1', firstName: '', lastName: '', externalEmail: '' }]);
    setPartnerUserRole('Staff');
    setPartnerClientIds([]);
    setPartnerPermissionOverrides(new Map());
    setPartnerPermissionsOpen(false);
    setExpandedPermissionCategories(new Set());
    setSendWelcomeEmail(true);

    // One-time credential links must not outlive the visit that created them
    setCreatedCredentials(null);
    setResetCredentials(null);
    setBulkCreateErrors([]);

    // Partner users are cached per company as they are expanded. Drop the cache so
    // an expand re-reads instead of replaying what was true last time.
    setCompanyUsers({});
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target as Node)) {
        setAddDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const { data: internalUsers = [], isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'users', 'all'],
    queryFn: async () => {
      const { data } = await api.get('/admin/users?status=all');
      return data.data as User[];
    },
    enabled: isOpen,
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data } = await api.get('/clients');
      return data.data as Client[];
    },
    enabled: isOpen,
  });

  const { data: permissionDefinitions = {}, isLoading: permissionsLoading } = useQuery<PermissionDefinitions>({
    queryKey: ['permissions', 'definitions'],
    queryFn: async () => {
      const response = await api.get<PermissionDefinitions>('/permissions');
      return response.data as unknown as PermissionDefinitions;
    },
    enabled: isOpen && !!showAddPartnerUserForm,
    staleTime: 5 * 60 * 1000,
  });

  const { data: emails = [], isLoading: emailsLoading } = useQuery({
    queryKey: ['allowlist', 'emails'],
    queryFn: async () => {
      const { data } = await api.get('/allowlist/emails');
      return data.data as AllowedEmail[];
    },
    enabled: isOpen,
  });

  // External Partners queries - Company Hierarchy
  const { data: partnerStatus } = useQuery({
    queryKey: ['external-partners', 'status'],
    queryFn: getExternalPartnerStatus,
    enabled: isOpen,
    staleTime: 60000, // Cache for 1 minute
  });

  const { data: partnerCompanies = [], isLoading: partnersLoading } = useQuery({
    queryKey: ['external-partners', 'companies'],
    queryFn: listPartnerCompanies,
    enabled: isOpen && partnerStatus?.configured && partnerStatus?.connected,
  });

  // Auto-sync External Partners when tab opens (max once per 5 minutes)
  useEffect(() => {
    const SYNC_COOLDOWN = 5 * 60 * 1000; // 5 minutes
    if (activeDataset === 'external' && partnerStatus?.configured && !isSyncing) {
      const now = Date.now();
      if (now - lastSyncTime > SYNC_COOLDOWN) {
        setIsSyncing(true);
        syncFromGoogle()
          .then(() => {
            setLastSyncTime(Date.now());
            queryClient.invalidateQueries({ queryKey: ['external-partners'] });
          })
          .catch(console.error)
          .finally(() => setIsSyncing(false));
      }
    }
  }, [activeDataset, partnerStatus?.configured, lastSyncTime, isSyncing, queryClient]);

  // Load users when company is expanded
  const loadCompanyUsers = async (companyId: string) => {
    if (companyUsers[companyId]) return; // Already loaded
    try {
      const users = await listPartnerUsers(companyId);
      setCompanyUsers(prev => ({ ...prev, [companyId]: users }));
    } catch (err) {
      console.error('Failed to load company users:', err);
    }
  };

  // Auto-load users when company is expanded
  useEffect(() => {
    if (expandedCompanyId && !companyUsers[expandedCompanyId]) {
      loadCompanyUsers(expandedCompanyId);
    }
  }, [expandedCompanyId]);

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  const updateUser = useMutation({
    mutationFn: async ({ userId, updates }: { userId: string; updates: { clientIds?: string[] } }) => {
      const { data } = await api.patch(`/admin/users/${userId}`, updates);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const activateUser = useMutation({
    mutationFn: async ({ userId, clientIds }: { userId: string; clientIds: string[] }) => {
      const { data } = await api.post(`/admin/users/${userId}/activate`, { clientIds });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const deactivateUser = useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post(`/admin/users/${userId}/deactivate`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.delete(`/admin/users/${userId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('User deleted', 'User has been permanently removed from the system');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Failed to delete user';
      toast.error('Delete failed', message);
    },
  });

  const addEmailMutation = useMutation({
    mutationFn: async (data: typeof newEmail) => {
      const response = await api.post('/allowlist/emails', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['allowlist', 'emails'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setShowAddUserForm(false);
      toast.success('User Added', `${variables.name || variables.email} has been invited. Welcome email sent.`);
      setNewEmail({ email: '', name: '', autoActivate: true, notes: '', provisioningData: { role: 'Staff', clientIds: [] } });
      setAddUserError(null);
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to add user';
      setAddUserError(message);
    },
  });

  const updateEmailMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AllowedEmail> }) => {
      const response = await api.put(`/allowlist/emails/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowlist', 'emails'] });
      setEditingEmail(null);
    },
  });

  const deleteEmailMutation = useMutation({
    mutationFn: async (id: string) => await api.delete(`/allowlist/emails/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['allowlist', 'emails'] }),
  });

  // External Partner mutations - Company Hierarchy
  const createCompanyMutation = useMutation({
    mutationFn: (input: CreateCompanyInput) => {
      // Filter out empty strings for optional fields (backend expects valid email or undefined)
      const cleanedInput: CreateCompanyInput = {
        name: input.name,
        ...(input.description?.trim() && { description: input.description.trim() }),
        ...(input.primaryContact?.trim() && { primaryContact: input.primaryContact.trim() }),
      };
      return createPartnerCompany(cleanedInput);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-partners'] });
      setShowAddCompanyForm(false);
      setNewCompany({ name: '', description: '', primaryContact: '' });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to create company';
      toast.error('Failed to create company', message);
    },
  });

  const archiveCompanyMutation = useMutation({
    mutationFn: archivePartnerCompany,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['external-partners'] }),
  });

  const unarchiveCompanyMutation = useMutation({
    mutationFn: unarchivePartnerCompany,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['external-partners'] }),
  });

  const syncFromGoogleMutation = useMutation({
    mutationFn: syncFromGoogle,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['external-partners'] });
      toast.success('Sync Complete', `${result.companiesImported} companies imported, ${result.usersSynced} users synced`);
    },
    onError: (error: any) => {
      toast.error('Sync Failed', error.message || 'Unknown error');
    },
  });

  const createPartnerUsersMutation = useMutation({
    mutationFn: ({ partnerId, input }: { partnerId: string; input: BulkCreateUsersInput }) =>
      createPartnerUsers(partnerId, input),
    onMutate: () => {
      setCreatedCredentials(null);
      setBulkCreateErrors([]);
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['external-partners'] });

      const successes = result.results.filter(r => r.status === 'created' && r.user) as Array<{
        status: 'created';
        user: PartnerUser;
        credentialLink: string;
        credentialExpiresAt: string;
        input?: { firstName: string; lastName: string; externalEmail?: string };
      }>;
      const failures = result.results.filter(r => r.status === 'failed');

      if (successes.length > 0) {
        setCompanyUsers(prev => ({
          ...prev,
          [variables.partnerId]: [
            ...(prev[variables.partnerId] || []),
            ...successes.map(s => s.user),
          ],
        }));
      }

      setCreatedCredentials(successes.map(s => ({
        email: s.user.email,
        credentialLink: s.credentialLink,
        credentialExpiresAt: s.credentialExpiresAt,
        externalEmail: s.input?.externalEmail,
      })));
      setBulkCreateErrors(
        failures.map(f => `${f.input?.firstName || 'User'} ${f.input?.lastName || ''}: ${f.error || 'Failed'}`)
      );

      if (failures.length === 0) {
        setShowAddPartnerUserForm(null);
        setNewPartnerUsers([{ id: 'row-1', firstName: '', lastName: '', externalEmail: '' }]);
        setPartnerUserRole('Staff');
        setPartnerClientIds([]);
        setPartnerPermissionOverrides(new Map());
        setPartnerPermissionsOpen(false);
        setExpandedPermissionCategories(new Set());
        setSendWelcomeEmail(true);
      }
    },
  });

  const resetPartnerUserPasswordMutation = useMutation({
    mutationFn: ({ partnerId, userId, sendEmail }: { partnerId: string; userId: string; sendEmail: boolean }) =>
      resetPartnerUserPassword(partnerId, userId, sendEmail),
    onSuccess: (result, variables) => {
      const user = companyUsers[variables.partnerId]?.find(u => u.id === variables.userId);
      setResetCredentials({
        email: user?.email || 'Unknown',
        credentialLink: result.credentialLink,
        credentialExpiresAt: result.credentialExpiresAt,
        externalEmail: user?.externalEmail,
      });
    },
  });

  const suspendPartnerUserMutation = useMutation({
    mutationFn: ({ partnerId, userId, reason }: { partnerId: string; userId: string; reason?: string }) =>
      suspendPartnerUser(partnerId, userId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['external-partners'] });
      // Update local cache
      setCompanyUsers(prev => ({
        ...prev,
        [variables.partnerId]: prev[variables.partnerId]?.map(u =>
          u.id === variables.userId ? { ...u, status: 'Suspended' as const } : u
        ) || [],
      }));
    },
  });

  const unsuspendPartnerUserMutation = useMutation({
    mutationFn: ({ partnerId, userId }: { partnerId: string; userId: string }) =>
      unsuspendPartnerUser(partnerId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['external-partners'] });
      // Update local cache
      setCompanyUsers(prev => ({
        ...prev,
        [variables.partnerId]: prev[variables.partnerId]?.map(u =>
          u.id === variables.userId ? { ...u, status: 'Active' as const } : u
        ) || [],
      }));
    },
  });

  const deletePartnerUserMutation = useMutation({
    mutationFn: ({ partnerId, userId }: { partnerId: string; userId: string }) =>
      deletePartnerUser(partnerId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['external-partners'] });
      // Update local cache
      setCompanyUsers(prev => ({
        ...prev,
        [variables.partnerId]: prev[variables.partnerId]?.filter(u => u.id !== variables.userId) || [],
      }));
    },
  });

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // Unified internal people list (users + pre-invited emails that haven't logged in)
  const internalPeople = useMemo((): InternalPerson[] => {
    const userEmails = new Set(internalUsers.map(u => u.email.toLowerCase()));

    // Convert users to InternalPerson
    const userPeople: InternalPerson[] = internalUsers.map(u => ({
      id: u.id,
      type: 'user' as const,
      name: u.name,
      email: u.email,
      picture: u.picture,
      role: u.role,
      isActive: u.isActive,
      status: u.isActive ? 'active' as const : 'pending' as const,
      lastLoginAt: u.lastLoginAt,
      clientAccess: u.clientAccess,
      originalUser: u,
    }));

    // Convert pre-invited emails (that haven't logged in) to InternalPerson
    const preInvitedPeople: InternalPerson[] = emails
      .filter(e => !e.hasUser) // Only those who haven't logged in yet
      .map(e => ({
        id: `email-${e.id}`,
        type: 'pre-invited' as const,
        name: e.name || e.email.split('@')[0],
        email: e.email,
        isActive: false,
        status: 'pre-invited' as const,
        autoActivate: e.autoActivate,
        notes: e.notes || undefined,
        originalEmail: e,
      }));

    return [...userPeople, ...preInvitedPeople];
  }, [internalUsers, emails]);

  const counts = useMemo(() => {
    const pending = internalUsers.filter(u => !u.isActive).length;
    const active = internalUsers.filter(u => u.isActive).length;
    const admins = internalUsers.filter(u => u.isActive && u.role === 'Admin').length;
    const staff = internalUsers.filter(u => u.isActive && u.role === 'Staff').length;
    const preInvited = emails.filter(e => !e.hasUser).length;
    const activeCompanies = partnerCompanies.filter(c => c.isActive).length;
    const inactiveCompanies = partnerCompanies.filter(c => !c.isActive).length;
    const totalPartnerUsers = partnerCompanies.reduce((sum, c) => sum + c.userCount, 0);
    const activePartnerUsers = partnerCompanies.reduce((sum, c) => sum + (c.activeUserCount || 0), 0);
    return {
      pending, active, admins, staff, preInvited,
      totalInternal: internalPeople.length,
      totalCompanies: partnerCompanies.length,
      activeCompanies,
      inactiveCompanies,
      totalPartnerUsers,
      activePartnerUsers,
    };
  }, [internalUsers, emails, partnerCompanies, internalPeople]);

  const filteredInternalPeople = useMemo(() => {
    let result = internalPeople;

    // Apply filter
    if (internalFilter === 'active') result = result.filter(p => p.status === 'active');
    else if (internalFilter === 'pre-invited') result = result.filter(p => p.status === 'pre-invited');
    else if (internalFilter === 'admin') result = result.filter(p => p.type === 'user' && p.role === 'Admin');
    else if (internalFilter === 'staff') result = result.filter(p => p.type === 'user' && p.role === 'Staff');

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(term) ||
        p.email.toLowerCase().includes(term)
      );
    }

    // Sort: active users first, then pending, then pre-invited
    return result.sort((a, b) => {
      const statusOrder = { active: 0, pending: 1, 'pre-invited': 2 };
      return statusOrder[a.status] - statusOrder[b.status];
    });
  }, [internalPeople, internalFilter, searchTerm]);

  const filteredCompanies = useMemo(() => {
    let result = partnerCompanies;
    if (externalFilter === 'active') result = partnerCompanies.filter(c => c.isActive);
    else if (externalFilter === 'inactive') result = partnerCompanies.filter(c => !c.isActive);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(term) ||
        c.description?.toLowerCase().includes(term) ||
        c.primaryContact?.toLowerCase().includes(term)
      );
    }
    return result;
  }, [partnerCompanies, externalFilter, searchTerm]);

  const hasPartnerUserInputErrors = newPartnerUsers.some(
    row => !row.firstName.trim() || !row.lastName.trim()
  );
  const missingClientsForStaff = partnerUserRole === 'Staff' && partnerClientIds.length === 0;
  const missingExternalEmails = sendWelcomeEmail && newPartnerUsers.some(
    row => !row.externalEmail?.trim()
  );
  const canCreatePartnerUsers = !hasPartnerUserInputErrors && !missingClientsForStaff;

  // ============================================================================
  // USER HANDLERS
  // ============================================================================

  const handleClientToggle = (userId: string, clientId: string, currentAccess: ClientAccess[], isPending: boolean) => {
    if (isPending) {
      const currentIds = pendingActivation[userId] || [];
      const newIds = currentIds.includes(clientId) ? currentIds.filter(id => id !== clientId) : [...currentIds, clientId];
      setPendingActivation(prev => ({ ...prev, [userId]: newIds }));
    } else {
      const currentIds = pendingChanges[userId]?.clientIds ?? currentAccess.filter(c => c.hasAccess).map(c => c.clientId);
      const newIds = currentIds.includes(clientId) ? currentIds.filter(id => id !== clientId) : [...currentIds, clientId];
      setPendingChanges(prev => ({ ...prev, [userId]: { ...prev[userId], clientIds: newIds } }));
    }
  };

  const handleSelectAllClients = (userId: string, selectAll: boolean, isPending: boolean) => {
    const newIds = selectAll ? clients.map(c => c.id) : [];
    if (isPending) setPendingActivation(prev => ({ ...prev, [userId]: newIds }));
    else setPendingChanges(prev => ({ ...prev, [userId]: { ...prev[userId], clientIds: newIds } }));
  };

  const handleSaveUser = async (userId: string) => {
    const changes = pendingChanges[userId];
    if (!changes) return;
    await updateUser.mutateAsync({ userId, updates: changes });
    setPendingChanges(prev => { const { [userId]: _, ...rest } = prev; return rest; });
  };

  const handleActivateUser = async (userId: string, user: User) => {
    const clientIds = pendingActivation[userId] || [];
    if (user.role === 'Staff' && clientIds.length === 0) {
      toast.warning('Client Required', 'Staff users must be assigned at least one client before activation.');
      return;
    }
    await activateUser.mutateAsync({ userId, clientIds });
    setPendingActivation(prev => { const { [userId]: _, ...rest } = prev; return rest; });
  };

  const handleDeactivateUser = async (userId: string) => {
    confirm({
      title: 'Deactivate User',
      message: 'Are you sure you want to deactivate this user? They will lose access to the system.',
      confirmLabel: 'Deactivate',
      type: 'danger',
      onConfirm: async () => {
        await deactivateUser.mutateAsync(userId);
      },
    });
  };

  const getClientAccessState = (user: User, clientId: string, isPending: boolean): boolean => {
    if (isPending) {
      // Use pendingActivation state if editing, otherwise use database clientAccess
      return pendingActivation[user.id]?.includes(clientId) ?? user.clientAccess?.find(c => c.clientId === clientId)?.hasAccess ?? false;
    }
    if (pendingChanges[user.id]?.clientIds) return pendingChanges[user.id].clientIds!.includes(clientId);
    return user.clientAccess?.find(c => c.clientId === clientId)?.hasAccess ?? false;
  };

  const getAccessCount = (user: User, isPending: boolean): number => {
    if (isPending) {
      // Use pendingActivation state if editing, otherwise use database clientAccess
      return pendingActivation[user.id]?.length ?? user.clientAccess?.filter(c => c.hasAccess).length ?? 0;
    }
    if (pendingChanges[user.id]?.clientIds) return pendingChanges[user.id].clientIds!.length;
    return user.clientAccess?.filter(c => c.hasAccess).length ?? 0;
  };

  // ============================================================================
  // PARTNER USER FORM HELPERS
  // ============================================================================

  const resetPartnerUserForm = () => {
    setNewPartnerUsers([{ id: 'row-1', firstName: '', lastName: '', externalEmail: '' }]);
    setPartnerUserRole('Staff');
    setPartnerClientIds([]);
    setPartnerPermissionOverrides(new Map());
    setPartnerPermissionsOpen(false);
    setExpandedPermissionCategories(new Set());
    setSendWelcomeEmail(true);
    setBulkCreateErrors([]);
  };

  const addPartnerUserRow = () => {
    setNewPartnerUsers(prev => ([
      ...prev,
      { id: `row-${Date.now()}-${prev.length}`, firstName: '', lastName: '', externalEmail: '' },
    ]));
  };

  const updatePartnerUserRow = (id: string, field: keyof NewPartnerUserRow, value: string) => {
    setNewPartnerUsers(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const removePartnerUserRow = (id: string) => {
    setNewPartnerUsers(prev => {
      if (prev.length === 1) return prev;
      return prev.filter(row => row.id !== id);
    });
  };

  const togglePartnerClient = (clientId: string) => {
    setPartnerClientIds(prev => prev.includes(clientId)
      ? prev.filter(id => id !== clientId)
      : [...prev, clientId]
    );
  };

  const getPermissionDefault = (permission: PermissionDefinition): boolean => {
    return partnerUserRole === 'Admin' ? permission.adminDefault : permission.staffDefault;
  };

  const togglePartnerPermission = (permission: PermissionDefinition) => {
    const defaultValue = getPermissionDefault(permission);
    const currentValue = partnerPermissionOverrides.has(permission.id)
      ? partnerPermissionOverrides.get(permission.id)!
      : defaultValue;
    const nextValue = !currentValue;

    setPartnerPermissionOverrides(prev => {
      const next = new Map(prev);
      if (nextValue === defaultValue) {
        next.delete(permission.id);
      } else {
        next.set(permission.id, nextValue);
      }
      return next;
    });
  };

  const partnerUserOverrides = Array.from(partnerPermissionOverrides.entries()).map(([permissionId, granted]) => ({
    permissionId,
    granted,
  }));

  // ============================================================================
  // RENDER
  // ============================================================================

  const isLoading = usersLoading || emailsLoading || partnersLoading;

  return (
    <>
    <AnimatedModal isOpen={isOpen} onClose={onClose} size="full">
      {/* ================================================================ */}
      {/* HEADER - Search prominent at top */}
      {/* ================================================================ */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-200 flex-shrink-0">
          {/* Top row: Search + Close */}
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={activeDataset === 'internal' ? 'Search people by name or email...' : 'Search partners...'}
                className="h-10 w-full pl-10 pr-4 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-gray-50 focus:bg-white transition-colors"
                aria-label="Search"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="group/tip absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <i className="fas fa-times text-xs" />
                  <ActionLabel text="Clear search" placement="bottom" />
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="group/tip relative h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close user management"
            >
              <i className="fas fa-times text-lg" />
              <ActionLabel text="Close user management" placement="bottom" />
            </button>
          </div>

          {/* Tab navigation - Two main tabs */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setActiveDataset('internal'); setSearchTerm(''); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                activeDataset === 'internal'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <i className="fas fa-users" />
              <span>Employees</span>
              <span className={`px-1.5 py-0.5 rounded text-xs ${
                activeDataset === 'internal' ? 'bg-blue-500 text-blue-100' : 'bg-gray-200 text-gray-600'
              }`}>
                {counts.totalInternal}
              </span>
              {counts.pending > 0 && activeDataset !== 'internal' && (
                <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
              )}
            </button>

            {partnerStatus?.configured && (
              <button
                onClick={() => { setActiveDataset('external'); setSearchTerm(''); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeDataset === 'external'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <i className="fas fa-building" />
                <span>Partners</span>
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  activeDataset === 'external' ? 'bg-blue-500 text-blue-100' : 'bg-gray-200 text-gray-600'
                }`}>
                  {counts.totalCompanies}
                </span>
              </button>
            )}

            {/* Add button - right aligned */}
            <div className="ml-auto">
              {activeDataset === 'internal' && (
                <button
                  onClick={() => setShowAddUserForm(true)}
                  className="h-9 px-3 border border-blue-600 text-blue-600 hover:bg-blue-50 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <i className="fas fa-user-plus text-xs" /> Invite Employee
                </button>
              )}
              {activeDataset === 'external' && partnerStatus?.configured && (
                <button
                  onClick={() => setShowAddCompanyForm(true)}
                  className="h-9 px-3 border border-blue-600 text-blue-600 hover:bg-blue-50 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <i className="fas fa-building text-xs" /> Add Partner
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* FILTER BAR + STATS */}
        {/* ================================================================ */}
        <div className="h-11 px-4 flex items-center justify-between border-b border-gray-100 bg-gray-50 flex-shrink-0">
          {/* Context-sensitive filters */}
          <div className="flex items-center gap-1">
            {activeDataset === 'internal' && (
              <>
                {([
                  { key: 'all', label: 'All' },
                  { key: 'active', label: 'Active', count: counts.active },
                  { key: 'pre-invited', label: 'Invited', count: counts.preInvited },
                  { key: 'admin', label: 'Admin', count: counts.admins },
                  { key: 'staff', label: 'Staff', count: counts.staff },
                ] as { key: InternalFilter; label: string; count?: number; highlight?: boolean }[]).map(f => (
                  <button
                    key={f.key}
                    onClick={() => setInternalFilter(f.key)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                      internalFilter === f.key
                        ? f.highlight && f.count && f.count > 0 ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {f.label}
                    {f.count !== undefined && (
                      <span className={`text-xs ${internalFilter === f.key ? '' : 'text-gray-400'}`}>
                        {f.count}
                      </span>
                    )}
                    {f.highlight && f.count !== undefined && f.count > 0 && internalFilter !== f.key && (
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                    )}
                  </button>
                ))}
              </>
            )}
            {activeDataset === 'external' && (
              <>
                {([
                  { key: 'all', label: 'All' },
                  { key: 'active', label: 'Active', count: counts.activePartnerUsers },
                  { key: 'inactive', label: 'Inactive', count: counts.inactiveCompanies },
                ] as { key: ExternalFilter; label: string; count?: number }[]).map(f => (
                  <button
                    key={f.key}
                    onClick={() => setExternalFilter(f.key)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                      externalFilter === f.key
                        ? f.key === 'inactive' && f.count && f.count > 0 ? 'bg-gray-200 text-gray-800' : 'bg-blue-100 text-blue-800'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {f.label}
                    {f.count !== undefined && (
                      <span className={`text-xs ${externalFilter === f.key ? '' : 'text-gray-400'}`}>
                        {f.count}
                      </span>
                    )}
                  </button>
                ))}
                {isSyncing && (
                  <span className="ml-2 px-2 py-1 text-xs text-gray-400 flex items-center gap-1">
                    <i className="fas fa-sync-alt animate-spin" />
                    Syncing...
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* ================================================================ */}
        {/* CONTENT AREA */}
        {/* ================================================================ */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <WaveLoader size="lg" />
            </div>
          ) : (
            <>
              {/* INTERNAL EMPLOYEES TABLE */}
              {activeDataset === 'internal' && (
                <div>
                  {/* Add Email Form - shown at top when adding */}
                  {showAddUserForm && (
                    <div className="m-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900">Invite Employee</h4>
                        <button
                          onClick={() => setShowAddUserForm(false)}
                          className="group/tip relative text-gray-400 hover:text-gray-600"
                          aria-label="Close invite form"
                        >
                          <i className="fas fa-times" />
                          <ActionLabel text="Close invite form" placement="bottom" />
                        </button>
                      </div>
                      <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-xs text-amber-700">
                          <i className="fas fa-info-circle mr-1" />
                          Employees can only be invited from <strong>@agilitybilling.com</strong> or <strong>@proviarcm.com</strong> domains.
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="email"
                          placeholder="user@agilitybilling.com"
                          value={newEmail.email}
                          onChange={(e) => setNewEmail({ ...newEmail, email: e.target.value })}
                          className={`h-8 px-3 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 ${
                            newEmail.email && !['agilitybilling.com', 'proviarcm.com', 'botpros.ai'].some(d => newEmail.email.toLowerCase().endsWith('@' + d))
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-300'
                          }`}
                        />
                        <input
                          type="text"
                          placeholder="Name"
                          value={newEmail.name}
                          onChange={(e) => setNewEmail({ ...newEmail, name: e.target.value })}
                          className="h-8 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      {newEmail.email && !['agilitybilling.com', 'proviarcm.com', 'botpros.ai'].some(d => newEmail.email.toLowerCase().endsWith('@' + d)) && (
                        <p className="mt-1 text-xs text-red-600">
                          <i className="fas fa-exclamation-circle mr-1" />
                          Email must be from an allowed domain (agilitybilling.com or proviarcm.com)
                        </p>
                      )}
                      <input
                        type="text"
                        placeholder="Notes"
                        value={newEmail.notes}
                        onChange={(e) => setNewEmail({ ...newEmail, notes: e.target.value })}
                        className="mt-3 h-8 w-full px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />

                      {/* Role Selection */}
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="radio"
                              name="newUserRole"
                              checked={newEmail.provisioningData?.role === 'Staff'}
                              onChange={() => setNewEmail({ ...newEmail, provisioningData: { ...newEmail.provisioningData, role: 'Staff' } })}
                              className="w-4 h-4 text-blue-600"
                            />
                            Staff
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="radio"
                              name="newUserRole"
                              checked={newEmail.provisioningData?.role === 'Admin'}
                              onChange={() => setNewEmail({ ...newEmail, provisioningData: { ...newEmail.provisioningData, role: 'Admin' } })}
                              className="w-4 h-4 text-blue-600"
                            />
                            Admin
                          </label>
                        </div>
                      </div>

                      {/* Client Access Selection */}
                      {newEmail.provisioningData?.role === 'Staff' && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-medium text-gray-600">Client Access</label>
                            <button
                              type="button"
                              onClick={() => {
                                const allClientIds = clients.map(c => c.id);
                                const currentIds = newEmail.provisioningData?.clientIds || [];
                                const newIds = currentIds.length === allClientIds.length ? [] : allClientIds;
                                setNewEmail({ ...newEmail, provisioningData: { ...newEmail.provisioningData, clientIds: newIds } });
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              {(newEmail.provisioningData?.clientIds?.length || 0) === clients.length ? 'Deselect All' : 'Select All'}
                            </button>
                          </div>
                          <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md bg-white p-2">
                            {clients.map(client => (
                              <label key={client.id} className="flex items-center gap-2 py-1 text-sm hover:bg-gray-50 rounded px-1">
                                <input
                                  type="checkbox"
                                  checked={newEmail.provisioningData?.clientIds?.includes(client.id) || false}
                                  onChange={(e) => {
                                    const currentIds = newEmail.provisioningData?.clientIds || [];
                                    const newIds = e.target.checked
                                      ? [...currentIds, client.id]
                                      : currentIds.filter(id => id !== client.id);
                                    setNewEmail({ ...newEmail, provisioningData: { ...newEmail.provisioningData, clientIds: newIds } });
                                  }}
                                  className="w-4 h-4 text-blue-600 rounded"
                                />
                                {client.name}
                              </label>
                            ))}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {newEmail.provisioningData?.clientIds?.length || 0} of {clients.length} clients selected
                          </p>
                        </div>
                      )}
                      {newEmail.provisioningData?.role === 'Admin' && (
                        <p className="mt-3 text-xs text-gray-500 italic">
                          <i className="fas fa-info-circle mr-1" />
                          Admins have access to all clients
                        </p>
                      )}

                      {addUserError && (
                        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-xs text-red-700">
                            <i className="fas fa-exclamation-circle mr-1" />
                            {addUserError}
                          </p>
                        </div>
                      )}
                      <div className="mt-3 flex items-center justify-end">
                        <button
                          onClick={() => {
                            setAddUserError(null);
                            addEmailMutation.mutate(newEmail);
                          }}
                          disabled={!newEmail.email || addEmailMutation.isPending || !['agilitybilling.com', 'proviarcm.com', 'botpros.ai'].some(d => newEmail.email.toLowerCase().endsWith('@' + d))}
                          className="h-8 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                        >
                          {addEmailMutation.isPending ? 'Adding...' : 'Add User'}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="divide-y divide-gray-100">
                    {/* Table Header */}
                    <div className="h-10 px-4 flex items-center bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide sticky top-0 z-10">
                      <div className="flex-1 min-w-0">Name</div>
                      <div className="w-24 text-center">Role</div>
                      <div className="w-28 text-center">Access</div>
                      <div className="w-24 text-center">Status</div>
                      <div className="w-32 text-right">Actions</div>
                    </div>

                    {filteredInternalPeople.length === 0 ? (
                      <div className="py-12 text-center text-gray-500">
                        <i className="fas fa-users text-3xl mb-2 opacity-40" />
                        <p className="text-sm">No people found</p>
                      </div>
                    ) : (
                      filteredInternalPeople.map(person => {
                        // For user-type people, use original user logic
                        if (person.type === 'user' && person.originalUser) {
                          const user = person.originalUser;
                          const isPending = !user.isActive && !user.activatedAt; // Never activated
                          const isInactive = !user.isActive && !!user.activatedAt; // Was deactivated
                          const isExpanded = expandedUserId === user.id;
                          // For pending users, use pendingActivation state; for inactive/active, use pendingChanges or existing access
                          const accessCount = getAccessCount(user, isPending);
                          const hasChanges = !!pendingChanges[user.id];
                          const showClientPanel = isPending || isInactive || user.role === 'Staff';
                          // Which panel, if any, this row currently owns. The row and its panel
                          // share one tinted band with a left accent so the panel reads as
                          // hanging off this row rather than floating between two of them.
                          const openPanel = isExpanded && showClientPanel
                            ? 'clients'
                            : permissionsUserId === user.id && !isPending && user.role === 'Staff'
                            ? 'permissions'
                            : null;

                          return (
                            <div
                              key={person.id}
                              className={
                                openPanel === 'clients' ? `bg-blue-50/40 ${OPEN_PANEL_ACCENT_BLUE}`
                                : openPanel === 'permissions' ? `bg-purple-50/40 ${OPEN_PANEL_ACCENT_PURPLE}`
                                : hasChanges ? 'bg-blue-50/50'
                                : isPending ? 'bg-amber-50/30'
                                : isInactive ? 'bg-red-50/30'
                                : ''
                              }
                            >
                              {/* Row - h-11 = 44px */}
                              <div className={`h-11 px-4 flex items-center group ${openPanel ? '' : 'hover:bg-gray-50'}`}>
                                {/* Name */}
                                <div className="flex-1 min-w-0 flex items-center gap-2.5">
                                  {user.picture ? (
                                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                                      {user.name.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                  </div>
                                </div>

                                {/* Role */}
                                <div className="w-24 flex justify-center">
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                    user.role === 'Admin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {user.role}
                                  </span>
                                </div>

                                {/* Access */}
                                <div className="w-28 text-center text-sm text-gray-600">
                                  {user.role === 'Admin' && !isPending ? 'All clients' : `${accessCount} client${accessCount !== 1 ? 's' : ''}`}
                                </div>

                                {/* Status */}
                                <div className="w-24 flex justify-center">
                                  {isPending ? (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">Pending</span>
                                  ) : isInactive ? (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">Inactive</span>
                                  ) : (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">Active</span>
                                  )}
                                </div>

                                {/* Actions - faint at rest, full on hover or keyboard focus */}
                                <div className={`w-32 ${ROW_ACTIONS_CLASS}`}>
                              {showClientPanel && (
                                <button
                                  onClick={() => setExpandedUserId(isExpanded ? null : user.id)}
                                  className={`group/tip relative h-7 w-7 flex items-center justify-center rounded text-xs ${
                                    isExpanded ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                  }`}
                                  aria-expanded={isExpanded}
                                  aria-label={isExpanded ? 'Hide client access' : 'Manage client access'}
                                >
                                  <i className="fas fa-building" />
                                  <ActionLabel text={isExpanded ? 'Hide client access' : 'Manage client access'} />
                                </button>
                              )}
                              {isPending ? (
                                <>
                                  <button
                                    onClick={() => handleActivateUser(user.id, user)}
                                    disabled={user.role === 'Staff' && accessCount === 0}
                                    className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={user.role === 'Staff' && accessCount === 0 ? 'Assign clients first' : 'Activate user'}
                                  >
                                    Activate
                                  </button>
                                  <button
                                    onClick={() => confirm({
                                      title: 'Delete User',
                                      message: `Permanently delete ${user.name}? This cannot be undone.`,
                                      confirmLabel: 'Delete',
                                      type: 'danger',
                                      onConfirm: () => deleteUser.mutate(user.id),
                                    })}
                                    disabled={deleteUser.isPending}
                                    className="group/tip relative h-7 w-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded text-xs"
                                    aria-label="Delete user permanently"
                                  >
                                    <i className="fas fa-trash" />
                                    <ActionLabel text="Delete user permanently" />
                                  </button>
                                </>
                              ) : isInactive ? (
                                <>
                                  <button
                                    onClick={() => handleActivateUser(user.id, user)}
                                    disabled={user.role === 'Staff' && accessCount === 0}
                                    className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Reactivate user"
                                  >
                                    Reactivate
                                  </button>
                                  <button
                                    onClick={() => confirm({
                                      title: 'Delete User',
                                      message: `Permanently delete ${user.name}? This cannot be undone.`,
                                      confirmLabel: 'Delete',
                                      type: 'danger',
                                      onConfirm: () => deleteUser.mutate(user.id),
                                    })}
                                    disabled={deleteUser.isPending}
                                    className="group/tip relative h-7 w-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded text-xs"
                                    aria-label="Delete user permanently"
                                  >
                                    <i className="fas fa-trash" />
                                    <ActionLabel text="Delete user permanently" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  {hasChanges && (
                                    <button
                                      onClick={() => handleSaveUser(user.id)}
                                      className="h-7 px-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded"
                                    >
                                      Save
                                    </button>
                                  )}
                                  {user.role === 'Staff' && (
                                    <button
                                      onClick={() => setPermissionsUserId(permissionsUserId === user.id ? null : user.id)}
                                      className={`group/tip relative h-7 w-7 flex items-center justify-center rounded text-xs ${
                                        permissionsUserId === user.id ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                                      }`}
                                      aria-expanded={permissionsUserId === user.id}
                                      aria-label={permissionsUserId === user.id ? 'Hide permissions' : 'Edit permissions'}
                                    >
                                      <i className="fas fa-key" />
                                      <ActionLabel text={permissionsUserId === user.id ? 'Hide permissions' : 'Edit permissions'} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeactivateUser(user.id)}
                                    className="group/tip relative h-7 w-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded text-xs"
                                    aria-label="Deactivate user"
                                  >
                                    <i className="fas fa-user-slash" />
                                    <ActionLabel text="Deactivate user" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Expanded: Client Access Panel */}
                          {isExpanded && showClientPanel && (
                            <div className="px-4 pb-3">
                              <div className="ml-8 bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium text-gray-700">Client Access</span>
                                  <button
                                    onClick={() => handleSelectAllClients(user.id, accessCount !== clients.length, isPending)}
                                    className="text-xs text-blue-600 hover:text-blue-700"
                                  >
                                    {accessCount === clients.length ? 'Deselect All' : 'Select All'}
                                  </button>
                                </div>
                                {isPending && user.role === 'Staff' && (
                                  <p className="text-xs text-amber-600 mb-2">
                                    <i className="fas fa-info-circle mr-1" />
                                    Staff must have at least one client.
                                  </p>
                                )}
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
                                  {clients.map(client => {
                                    const hasAccess = getClientAccessState(user, client.id, isPending);
                                    return (
                                      <label
                                        key={client.id}
                                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${
                                          hasAccess ? 'bg-blue-100 border border-blue-200' : 'bg-white border border-gray-200 hover:border-gray-300'
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={hasAccess}
                                          onChange={() => handleClientToggle(user.id, client.id, user.clientAccess || [], isPending)}
                                          className="w-3.5 h-3.5 text-blue-600 rounded"
                                        />
                                        <span className={hasAccess ? 'text-blue-900 font-medium' : 'text-gray-700'}>{client.name}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Expanded: Permissions Panel */}
                          {permissionsUserId === user.id && !isPending && user.role === 'Staff' && (
                            <div className="px-4 pb-3">
                              <div className="ml-8 border border-purple-200 rounded-lg">
                                <UserPermissionsEditor userId={user.id} userName={user.name} userRole={user.role} onClose={() => setPermissionsUserId(null)} onRoleChanged={() => setPermissionsUserId(null)} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                        }

                        // For pre-invited type people (not yet logged in)
                        if (person.type === 'pre-invited' && person.originalEmail) {
                          const email = person.originalEmail;
                          const isEditingThisEmail = editingEmail?.id === email.id;
                          return (
                            <div
                              key={person.id}
                              className={isEditingThisEmail ? `bg-blue-50/40 ${OPEN_PANEL_ACCENT_BLUE}` : 'bg-gray-50/50'}
                            >
                              <div className={`h-11 px-4 flex items-center group ${isEditingThisEmail ? '' : 'hover:bg-gray-100'}`}>
                                {/* Name/Email */}
                                <div className="flex-1 min-w-0 flex items-center gap-2.5">
                                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                                    <i className="fas fa-envelope text-xs text-gray-400" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-600 truncate">{person.name}</p>
                                    <p className="text-xs text-gray-400 truncate">{person.email}</p>
                                  </div>
                                </div>

                                {/* Role - empty for pre-invited */}
                                <div className="w-24 flex justify-center">
                                  <span className="text-xs text-gray-400">—</span>
                                </div>

                                {/* Access - not applicable */}
                                <div className="w-28 text-center text-xs text-gray-400">
                                  —
                                </div>

                                {/* Status */}
                                <div className="w-24 flex justify-center">
                                  <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">
                                    Pre-Invited
                                  </span>
                                </div>

                                {/* Actions */}
                                <div className={`w-32 ${ROW_ACTIONS_CLASS}`}>
                                  <button
                                    onClick={() => setEditingEmail(isEditingThisEmail ? null : email)}
                                    className={`group/tip relative h-7 w-7 flex items-center justify-center rounded text-xs ${
                                      isEditingThisEmail ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                                    }`}
                                    aria-expanded={isEditingThisEmail}
                                    aria-label={isEditingThisEmail ? 'Close invite details' : 'Edit invite details'}
                                  >
                                    <i className="fas fa-pen" />
                                    <ActionLabel text={isEditingThisEmail ? 'Close invite details' : 'Edit invite details'} />
                                  </button>
                                  <button
                                    onClick={() => confirm({
                                      title: 'Remove Email',
                                      message: `Are you sure you want to remove ${email.email} from the allowlist?`,
                                      confirmLabel: 'Remove',
                                      type: 'danger',
                                      onConfirm: () => deleteEmailMutation.mutate(email.id),
                                    })}
                                    className="group/tip relative h-7 w-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded text-xs"
                                    aria-label="Remove invitation"
                                  >
                                    <i className="fas fa-trash" />
                                    <ActionLabel text="Remove invitation" />
                                  </button>
                                </div>
                              </div>

                              {/* Edit inline */}
                              {isEditingThisEmail && editingEmail && (
                                <div className="px-4 pb-3">
                                  <div className="ml-8 p-3 bg-white border border-gray-200 rounded-lg">
                                    <div className="grid grid-cols-2 gap-3">
                                      <input
                                        type="text"
                                        value={editingEmail.name || ''}
                                        onChange={(e) => setEditingEmail({ ...editingEmail, name: e.target.value })}
                                        placeholder="Name"
                                        className="h-8 px-3 text-sm border border-gray-300 rounded-md"
                                      />
                                      <input
                                        type="text"
                                        value={editingEmail.notes || ''}
                                        onChange={(e) => setEditingEmail({ ...editingEmail, notes: e.target.value })}
                                        placeholder="Notes"
                                        className="h-8 px-3 text-sm border border-gray-300 rounded-md"
                                      />
                                    </div>
                                    <div className="mt-3 flex items-center justify-end gap-2">
                                      <button onClick={() => setEditingEmail(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => {
                                          updateEmailMutation.mutate({
                                            id: editingEmail.id,
                                            data: { name: editingEmail.name, notes: editingEmail.notes, autoActivate: true },
                                          });
                                        }}
                                        disabled={updateEmailMutation.isPending}
                                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                      >
                                        {updateEmailMutation.isPending ? 'Saving...' : 'Save'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }

                        return null;
                      })
                    )}
                  </div>
                </div>
              )}

              {/* EXTERNAL PARTNER COMPANIES */}
              {activeDataset === 'external' && (
                <div>
                  {/* Service Status Banner */}
                  {partnerStatus && !partnerStatus.connected && (
                    <div className="m-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-800">
                        <i className="fas fa-exclamation-triangle" />
                        <span className="font-medium">External Partner Service Issue</span>
                      </div>
                      <p className="mt-1 text-sm text-amber-700">{partnerStatus.error || 'Service not connected'}</p>
                    </div>
                  )}

                  {/* Credential Links Display */}
                  {createdCredentials && createdCredentials.length > 0 && (
                    <div className="m-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 text-green-800">
                          <i className="fas fa-check-circle" />
                          <span className="font-medium">
                            {createdCredentials.length} User{createdCredentials.length > 1 ? 's' : ''} Created Successfully
                          </span>
                        </div>
                        <button
                          onClick={() => setCreatedCredentials(null)}
                          className="group/tip relative text-gray-400 hover:text-gray-600"
                          aria-label="Dismiss credential links"
                        >
                          <i className="fas fa-times" />
                          <ActionLabel text="Dismiss credential links" placement="bottom" />
                        </button>
                      </div>
                      {/* Email sent notification */}
                      {createdCredentials.some(c => c.externalEmail) && (
                        <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                          <i className="fas fa-envelope mr-2" />
                          Welcome email{createdCredentials.filter(c => c.externalEmail).length > 1 ? 's' : ''} sent to{' '}
                          {createdCredentials.filter(c => c.externalEmail).map((c, i, arr) => (
                            <span key={c.email}>
                              <span className="font-medium">{c.externalEmail}</span>
                              {i < arr.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="space-y-3 text-sm">
                        {createdCredentials.map((cred) => (
                          <div key={cred.email} className="bg-white border rounded p-2">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600 w-16">Email:</span>
                              <span className="font-mono bg-gray-50 px-2 py-1 rounded border flex-1 truncate">{cred.email}</span>
                              <button
                                onClick={() => navigator.clipboard.writeText(cred.email)}
                                className="group/tip relative text-blue-600 hover:text-blue-800"
                                aria-label="Copy email address"
                              >
                                <i className="fas fa-copy" />
                                <ActionLabel text="Copy email address" placement="bottom" />
                              </button>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-gray-600 w-16">Link:</span>
                              <span className="font-mono bg-gray-50 px-2 py-1 rounded border flex-1 truncate">{cred.credentialLink}</span>
                              <button
                                onClick={() => navigator.clipboard.writeText(cred.credentialLink)}
                                className="group/tip relative text-blue-600 hover:text-blue-800"
                                aria-label="Copy credential link"
                              >
                                <i className="fas fa-copy" />
                                <ActionLabel text="Copy credential link" placement="bottom" />
                              </button>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Expires {new Date(cred.credentialExpiresAt).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-xs text-amber-600">
                        <i className="fas fa-exclamation-triangle mr-1" />
                        Links are one-time use and expire in 24 hours.
                      </p>
                    </div>
                  )}

                  {bulkCreateErrors.length > 0 && (
                    <div className="m-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-800 mb-2">
                        <i className="fas fa-exclamation-triangle" />
                        <span className="text-sm font-medium">Some users could not be created</span>
                      </div>
                      <div className="text-xs text-amber-700 space-y-1">
                        {bulkCreateErrors.map((error, idx) => (
                          <div key={`${error}-${idx}`}>{error}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reset Password Credential Link Display */}
                  {resetCredentials && (
                    <div className="m-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 text-blue-800">
                          <i className="fas fa-key" />
                          <span className="font-medium">Password Reset Link</span>
                        </div>
                        <button
                          onClick={() => setResetCredentials(null)}
                          className="group/tip relative text-gray-400 hover:text-gray-600"
                          aria-label="Dismiss password reset link"
                        >
                          <i className="fas fa-times" />
                          <ActionLabel text="Dismiss password reset link" placement="bottom" />
                        </button>
                      </div>
                      {/* Email sent notification */}
                      {resetCredentials.externalEmail && (
                        <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                          <i className="fas fa-envelope-circle-check mr-2" />
                          Password reset email sent to <span className="font-medium">{resetCredentials.externalEmail}</span>
                        </div>
                      )}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600 w-16">Email:</span>
                          <span className="font-mono bg-white px-2 py-1 rounded border flex-1 truncate">{resetCredentials.email}</span>
                          <button
                            onClick={() => navigator.clipboard.writeText(resetCredentials.email)}
                            className="group/tip relative text-blue-600 hover:text-blue-800"
                            aria-label="Copy email address"
                          >
                            <i className="fas fa-copy" />
                            <ActionLabel text="Copy email address" placement="bottom" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600 w-16">Link:</span>
                          <span className="font-mono bg-white px-2 py-1 rounded border flex-1 truncate">{resetCredentials.credentialLink}</span>
                          <button
                            onClick={() => navigator.clipboard.writeText(resetCredentials.credentialLink)}
                            className="group/tip relative text-blue-600 hover:text-blue-800"
                            aria-label="Copy password reset link"
                          >
                            <i className="fas fa-copy" />
                            <ActionLabel text="Copy password reset link" placement="bottom" />
                          </button>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Expires {new Date(resetCredentials.credentialExpiresAt).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {/* Add Company Form */}
                  {showAddCompanyForm && (
                    <div className="m-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900">Create Partner Company</h4>
                        <button
                          onClick={() => setShowAddCompanyForm(false)}
                          className="group/tip relative text-gray-400 hover:text-gray-600"
                          aria-label="Close company form"
                        >
                          <i className="fas fa-times" />
                          <ActionLabel text="Close company form" placement="bottom" />
                        </button>
                      </div>
                      <input
                        type="text"
                        placeholder="Company Name (e.g., Acme Healthcare)"
                        value={newCompany.name}
                        onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                        className="h-8 w-full px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="email"
                        placeholder="Contact Email"
                        value={newCompany.primaryContact || ''}
                        onChange={(e) => setNewCompany({ ...newCompany, primaryContact: e.target.value })}
                        className="mt-3 h-8 w-full px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => createCompanyMutation.mutate(newCompany)}
                          disabled={!newCompany.name || createCompanyMutation.isPending}
                          className="h-8 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                        >
                          {createCompanyMutation.isPending ? 'Creating...' : 'Create Company'}
                        </button>
                      </div>
                      {createCompanyMutation.isError && (
                        <p className="mt-2 text-sm text-red-600">
                          <i className="fas fa-exclamation-circle mr-1" />
                          {(createCompanyMutation.error as Error)?.message || 'Failed to create company'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Companies List - Hierarchical */}
                  <div className="divide-y divide-gray-100">
                    <div className="h-10 px-4 flex items-center bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide sticky top-0 z-10">
                      <div className="w-8" /> {/* Expand chevron space */}
                      <div className="flex-1 min-w-0">Company</div>
                      <div className="w-24 text-center">Users</div>
                      <div className="w-32 text-center">Status</div>
                      <div className="w-40 text-right">Actions</div>
                    </div>

                    {filteredCompanies.length === 0 ? (
                      <div className="py-12 text-center text-gray-500">
                        <i className="fas fa-building text-3xl mb-2 opacity-40" />
                        <p className="text-sm">
                          {partnerCompanies.length === 0
                            ? 'No partner companies yet. Click "Add" to create one.'
                            : 'No companies match your filter'}
                        </p>
                      </div>
                    ) : (
                      filteredCompanies.map(company => {
                        const isExpanded = expandedCompanyId === company.id;
                        const users = companyUsers[company.id] || [];
                        const pendingUsers = users.filter(u => u.status === 'Pending').length;
                        const activeUsers = users.filter(u => u.status === 'Active').length;
                        const suspendedUsers = users.filter(u => u.status === 'Suspended').length;

                        return (
                          <div
                            key={company.id}
                            className={
                              isExpanded ? OPEN_PANEL_ACCENT_BLUE
                              : !company.isActive ? 'bg-gray-50/50'
                              : ''
                            }
                          >
                            {/* Company Row */}
                            <div className={`h-11 px-4 flex items-center group ${isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                              {/* Expand Button */}
                              <button
                                onClick={() => setExpandedCompanyId(isExpanded ? null : company.id)}
                                className={`group/tip relative w-8 flex items-center justify-center ${
                                  isExpanded ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'
                                }`}
                                aria-expanded={isExpanded}
                                aria-label={isExpanded ? 'Hide company users' : 'Show company users'}
                              >
                                <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-xs`} />
                                <ActionLabel text={isExpanded ? 'Hide company users' : 'Show company users'} align="left" />
                              </button>

                              {/* Company Name */}
                              <div className="flex-1 min-w-0 flex items-center gap-2.5">
                                <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                                  company.isActive ? 'bg-blue-100' : 'bg-gray-200'
                                }`}>
                                  <i className={`fas fa-building text-xs ${company.isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{company.name}</p>
                                  {company.description && (
                                    <p className="text-xs text-gray-500 truncate">{company.description}</p>
                                  )}
                                </div>
                              </div>

                              {/* User Count */}
                              <div className="w-24 text-center text-sm text-gray-600">
                                {company.userCount}
                              </div>

                              {/* Status Summary */}
                              <div className="w-32 flex justify-center gap-1.5">
                                {company.isActive ? (
                                  <>
                                    {company.activeUserCount > 0 && (
                                      <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                        {company.activeUserCount} active
                                      </span>
                                    )}
                                    {company.pendingUserCount > 0 && (
                                      <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                                        {company.pendingUserCount} pending
                                      </span>
                                    )}
                                    {company.userCount === 0 && (
                                      <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">No users</span>
                                    )}
                                  </>
                                ) : (
                                  <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Inactive</span>
                                )}
                              </div>

                              {/* Company Actions */}
                              <div className={`w-40 ${ROW_ACTIONS_CLASS}`}>
                                {company.isActive && (
                                  <button
                                    onClick={() => { setShowAddPartnerUserForm(company.id); setExpandedCompanyId(company.id); resetPartnerUserForm(); }}
                                    className="h-7 px-2 text-blue-600 hover:bg-blue-50 rounded text-xs font-medium flex items-center gap-1"
                                    title="Add user to company"
                                  >
                                    <i className="fas fa-user-plus" /> Add User
                                  </button>
                                )}
                                {company.isActive ? (
                                  <button
                                    onClick={() => confirm({
                                      title: 'Archive Company',
                                      message: `Are you sure you want to archive ${company.name}? This will deactivate the company and all its users.`,
                                      confirmLabel: 'Archive',
                                      type: 'warning',
                                      onConfirm: () => archiveCompanyMutation.mutate(company.id),
                                    })}
                                    disabled={archiveCompanyMutation.isPending}
                                    className="group/tip relative h-7 w-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded text-xs"
                                    aria-label="Archive company and its users"
                                  >
                                    <i className="fas fa-archive" />
                                    <ActionLabel text="Archive company and its users" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => confirm({
                                      title: 'Restore Company',
                                      message: `Are you sure you want to restore ${company.name}? This will reactivate the company.`,
                                      confirmLabel: 'Restore',
                                      type: 'success',
                                      onConfirm: () => unarchiveCompanyMutation.mutate(company.id),
                                    })}
                                    disabled={unarchiveCompanyMutation.isPending}
                                    className="h-7 px-2 text-green-600 hover:bg-green-50 rounded text-xs font-medium flex items-center gap-1"
                                    title="Restore company"
                                  >
                                    <i className="fas fa-undo" /> Restore
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Expanded: Users List + Add User Form */}
                            {isExpanded && (
                              <div className="bg-gray-50 border-t border-gray-100">
                                {/* Add Users Form (inline) */}
                                {showAddPartnerUserForm === company.id && (
                                  <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 space-y-3">
                                    <div className="flex items-center justify-between">
                                      <h5 className="text-xs font-medium text-gray-700">Add Users to {company.name}</h5>
                                      <button
                                        onClick={() => setShowAddPartnerUserForm(null)}
                                        className="group/tip relative text-gray-400 hover:text-gray-600 text-xs"
                                        aria-label="Close add-users form"
                                      >
                                        <i className="fas fa-times" />
                                        <ActionLabel text="Close add-users form" placement="bottom" />
                                      </button>
                                    </div>

                                    <div className="space-y-2">
                                      {newPartnerUsers.map((row, idx) => (
                                        <div key={row.id} className="flex gap-2 items-center">
                                          <input
                                            type="text"
                                            placeholder="First Name"
                                            value={row.firstName}
                                            onChange={(e) => updatePartnerUserRow(row.id, 'firstName', e.target.value)}
                                            className="h-7 w-36 px-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                          />
                                          <input
                                            type="text"
                                            placeholder="Last Name"
                                            value={row.lastName}
                                            onChange={(e) => updatePartnerUserRow(row.id, 'lastName', e.target.value)}
                                            className="h-7 w-36 px-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                          />
                                          <input
                                            type="email"
                                            placeholder="Personal Email"
                                            value={row.externalEmail || ''}
                                            onChange={(e) => updatePartnerUserRow(row.id, 'externalEmail', e.target.value)}
                                            className="h-7 flex-1 px-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                          />
                                          <button
                                            onClick={() => removePartnerUserRow(row.id)}
                                            disabled={newPartnerUsers.length === 1}
                                            className="group/tip relative h-7 w-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded text-xs disabled:opacity-30"
                                            aria-label={newPartnerUsers.length === 1 ? 'At least one row required' : `Remove row ${idx + 1}`}
                                          >
                                            <i className="fas fa-trash" />
                                            <ActionLabel text={newPartnerUsers.length === 1 ? 'At least one row required' : `Remove row ${idx + 1}`} placement="bottom" />
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        onClick={addPartnerUserRow}
                                        className="text-xs text-blue-700 hover:text-blue-900"
                                      >
                                        <i className="fas fa-plus mr-1" />
                                        Add Row
                                      </button>
                                    </div>

                                    <p className="text-xs text-gray-500">
                                      Emails will be generated as <span className="font-mono">
                                        {newPartnerUsers[0]?.firstName
                                          ? `${newPartnerUsers[0].firstName.toLowerCase().replace(/[^a-z]/g, '')}.${company.slug}@proviarcm.com`
                                          : `{firstname}.${company.slug}@proviarcm.com`
                                        }
                                      </span>
                                      {newPartnerUsers[0]?.firstName && (
                                        <span className="text-gray-400 ml-1">(e.g., first name + company)</span>
                                      )}
                                    </p>

                                    <div className="flex items-center gap-3">
                                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                        <i className="fas fa-user-shield mr-1" />
                                        Role: Staff (external partners cannot be admins)
                                      </span>
                                      <label className="flex items-center gap-2 text-xs text-gray-600">
                                        <input
                                          type="checkbox"
                                          checked={sendWelcomeEmail}
                                          onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                                          className="w-3.5 h-3.5 text-blue-600 rounded"
                                        />
                                        Send credential link email
                                      </label>
                                    </div>
                                    {missingExternalEmails && (
                                      <p className="text-xs text-amber-600">
                                        <i className="fas fa-info-circle mr-1" />
                                        Users without an external email will not receive the link.
                                      </p>
                                    )}

                                    <div className="space-y-2">
                                      <div className="text-xs font-medium text-gray-700">Client Access</div>
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
                                        {clients.map(client => (
                                          <label
                                            key={client.id}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${
                                              partnerClientIds.includes(client.id)
                                                ? 'bg-blue-100 border border-blue-200'
                                                : 'bg-white border border-gray-200 hover:border-gray-300'
                                            }`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={partnerClientIds.includes(client.id)}
                                              onChange={() => togglePartnerClient(client.id)}
                                              className="w-3.5 h-3.5 text-blue-600 rounded"
                                            />
                                            <span className={partnerClientIds.includes(client.id) ? 'text-blue-900 font-medium' : 'text-gray-700'}>
                                              {client.name}
                                            </span>
                                          </label>
                                        ))}
                                      </div>
                                      {missingClientsForStaff && (
                                        <p className="text-xs text-amber-600">
                                          <i className="fas fa-info-circle mr-1" />
                                          External partner users must be assigned at least one client.
                                        </p>
                                      )}
                                    </div>

                                    <div className="space-y-2">
                                      <button
                                        onClick={() => setPartnerPermissionsOpen(!partnerPermissionsOpen)}
                                        className="text-xs text-blue-700 hover:text-blue-900 flex items-center gap-2"
                                      >
                                        <i className={`fas fa-chevron-${partnerPermissionsOpen ? 'down' : 'right'}`} />
                                        Permissions
                                        {partnerPermissionOverrides.size > 0 && (
                                          <span className="text-gray-500">({partnerPermissionOverrides.size} overrides)</span>
                                        )}
                                      </button>
                                      {partnerPermissionsOpen && (
                                        <div className="bg-white border border-gray-200 rounded p-2">
                                          {permissionsLoading ? (
                                            <div className="text-xs text-gray-500">Loading permissions...</div>
                                          ) : (
                                            Object.entries(permissionDefinitions).map(([category, perms]) => (
                                              <div key={category} className="mb-2">
                                                <button
                                                  onClick={() => {
                                                    setExpandedPermissionCategories(prev => {
                                                      const next = new Set(prev);
                                                      if (next.has(category)) next.delete(category);
                                                      else next.add(category);
                                                      return next;
                                                    });
                                                  }}
                                                  className="flex items-center gap-2 text-xs font-medium text-gray-700"
                                                >
                                                  <i className={`fas fa-chevron-${expandedPermissionCategories.has(category) ? 'down' : 'right'}`} />
                                                  {category}
                                                </button>
                                                {expandedPermissionCategories.has(category) && (
                                                  <div className="mt-2 space-y-1 pl-4">
                                                    {perms.map(permission => {
                                                      const defaultValue = getPermissionDefault(permission);
                                                      const currentValue = partnerPermissionOverrides.has(permission.id)
                                                        ? partnerPermissionOverrides.get(permission.id)!
                                                        : defaultValue;
                                                      return (
                                                        <label key={permission.id} className="flex items-center gap-2 text-xs text-gray-600">
                                                          <input
                                                            type="checkbox"
                                                            checked={currentValue}
                                                            onChange={() => togglePartnerPermission(permission)}
                                                            className="w-3.5 h-3.5 text-blue-600 rounded"
                                                          />
                                                          <span className="text-gray-800">{permission.name}</span>
                                                          {currentValue !== defaultValue && (
                                                            <span className="text-amber-600">(override)</span>
                                                          )}
                                                        </label>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    {createPartnerUsersMutation.isError && (
                                      <p className="text-xs text-red-600">
                                        {(createPartnerUsersMutation.error as Error)?.message || 'Failed to create users'}
                                      </p>
                                    )}

                                    <div className="flex justify-end">
                                      <button
                                        onClick={() => createPartnerUsersMutation.mutate({
                                          partnerId: company.id,
                                          input: {
                                            users: newPartnerUsers.map(({ firstName, lastName, externalEmail }) => ({ firstName, lastName, externalEmail })),
                                            role: partnerUserRole,
                                            clientIds: partnerUserRole === 'Staff' ? partnerClientIds : undefined,
                                            permissionOverrides: partnerUserRole === 'Staff' && partnerUserOverrides.length > 0 ? partnerUserOverrides : undefined,
                                            sendWelcomeEmail,
                                          },
                                        })}
                                        disabled={!canCreatePartnerUsers || createPartnerUsersMutation.isPending}
                                        className="h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded disabled:opacity-50"
                                      >
                                        {createPartnerUsersMutation.isPending ? 'Creating...' : `Create ${newPartnerUsers.length} User${newPartnerUsers.length > 1 ? 's' : ''}`}
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Users Table */}
                                {users.length === 0 ? (
                                  <div className="px-4 py-6 text-center text-gray-500 text-sm">
                                    <i className="fas fa-users text-2xl mb-2 opacity-40" />
                                    <p>No users in this company yet</p>
                                  </div>
                                ) : (
                                  <div className="divide-y divide-gray-100">
                                    {/* Sort: Pending first, then Active, then Suspended */}
                                    {[...users].sort((a, b) => {
                                      const order = { Pending: 0, Active: 1, Suspended: 2 };
                                      return order[a.status] - order[b.status];
                                    }).map(user => (
                                      <React.Fragment key={user.id}>
                                      <div
                                        className={`h-10 px-4 pl-12 flex items-center group ${
                                          user.appUserId && partnerClientAccessUser?.appUserId === user.appUserId
                                            ? `bg-blue-50/60 ${OPEN_PANEL_ACCENT_BLUE}`
                                            : user.appUserId && partnerPermissionsUser?.appUserId === user.appUserId
                                            ? `bg-purple-50/60 ${OPEN_PANEL_ACCENT_PURPLE}`
                                            : `hover:bg-gray-100 ${
                                                user.status === 'Suspended' ? 'bg-red-50/50' : user.status === 'Pending' ? 'bg-amber-50/30' : ''
                                              }`
                                        }`}
                                      >
                                        {/* User Info */}
                                        <div className="flex-1 min-w-0 flex items-center gap-2">
                                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                            user.status === 'Active' ? 'bg-green-100' : user.status === 'Pending' ? 'bg-amber-100' : 'bg-red-100'
                                          }`}>
                                            <i className={`fas fa-user text-[10px] ${
                                              user.status === 'Active' ? 'text-green-600' : user.status === 'Pending' ? 'text-amber-600' : 'text-red-600'
                                            }`} />
                                          </div>
                                          <div className="min-w-0">
                                            <p className="text-sm text-gray-900 truncate">{user.fullName}</p>
                                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                          </div>
                                        </div>

                                        {/* Status */}
                                        <div className="w-20 text-center">
                                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                                            user.status === 'Active' ? 'bg-green-100 text-green-700' :
                                            user.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                                            'bg-red-100 text-red-700'
                                          }`}>
                                            {user.status}
                                          </span>
                                        </div>

                                        {/* User Actions */}
                                        <div className={`w-36 ${ROW_ACTIONS_CLASS}`}>
                                          {/* Client Access button - only for Staff users with appUserId */}
                                          {user.appUserId && user.role === 'Staff' && (
                                            <button
                                              onClick={() => {
                                                // Second click closes the panel, matching the
                                                // employee rows and the button's own active state
                                                if (partnerClientAccessUser?.appUserId === user.appUserId) {
                                                  setPartnerClientAccessUser(null);
                                                  setPartnerClientAccessPending([]);
                                                  return;
                                                }
                                                // Use clientIds directly from the partner user data
                                                const currentClientIds = user.clientIds || [];
                                                setPartnerClientAccessUser({
                                                  appUserId: user.appUserId!,
                                                  name: user.fullName,
                                                  currentClientIds,
                                                });
                                                setPartnerClientAccessPending(currentClientIds);
                                              }}
                                              className={`group/tip relative h-6 w-6 flex items-center justify-center rounded text-xs ${
                                                partnerClientAccessUser?.appUserId === user.appUserId
                                                  ? 'text-blue-600 bg-blue-100'
                                                  : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                                              }`}
                                              aria-expanded={partnerClientAccessUser?.appUserId === user.appUserId}
                                              aria-label={partnerClientAccessUser?.appUserId === user.appUserId ? 'Hide client access' : 'Edit client access'}
                                            >
                                              <i className="fas fa-building" />
                                              <ActionLabel text={partnerClientAccessUser?.appUserId === user.appUserId ? 'Hide client access' : 'Edit client access'} />
                                            </button>
                                          )}
                                          {/* Permissions button - only for Staff users with appUserId */}
                                          {user.appUserId && user.role === 'Staff' && (
                                            <button
                                              onClick={() => setPartnerPermissionsUser(
                                                partnerPermissionsUser?.appUserId === user.appUserId
                                                  ? null
                                                  : { appUserId: user.appUserId!, name: user.fullName, role: user.role! }
                                              )}
                                              className={`group/tip relative h-6 w-6 flex items-center justify-center rounded text-xs ${
                                                partnerPermissionsUser?.appUserId === user.appUserId
                                                  ? 'text-purple-600 bg-purple-100'
                                                  : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                                              }`}
                                              aria-expanded={partnerPermissionsUser?.appUserId === user.appUserId}
                                              aria-label={partnerPermissionsUser?.appUserId === user.appUserId ? 'Hide permissions' : 'Edit permissions'}
                                            >
                                              <i className="fas fa-shield-halved" />
                                              <ActionLabel text={partnerPermissionsUser?.appUserId === user.appUserId ? 'Hide permissions' : 'Edit permissions'} />
                                            </button>
                                          )}
                                          <button
                                            onClick={() => confirm({
                                              title: 'Reset Password',
                                              message: `Are you sure you want to reset the password for ${user.email}? They will receive a new credential link via email.`,
                                              confirmLabel: 'Reset Password',
                                              type: 'info',
                                              onConfirm: () => resetPartnerUserPasswordMutation.mutate({ partnerId: company.id, userId: user.id, sendEmail: true }),
                                            })}
                                            disabled={resetPartnerUserPasswordMutation.isPending}
                                            className="group/tip relative h-6 w-6 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded text-xs"
                                            aria-label="Reset password"
                                          >
                                            <i className="fas fa-key" />
                                            <ActionLabel text="Reset password" />
                                          </button>
                                          {user.status === 'Suspended' ? (
                                            <button
                                              onClick={() => unsuspendPartnerUserMutation.mutate({ partnerId: company.id, userId: user.id })}
                                              disabled={unsuspendPartnerUserMutation.isPending}
                                              className="h-6 px-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded disabled:opacity-50"
                                            >
                                              Unsuspend
                                            </button>
                                          ) : (
                                            <button
                                              onClick={() => confirm({
                                                title: 'Suspend User',
                                                message: `Are you sure you want to suspend ${user.email}? They will not be able to log in until unsuspended.`,
                                                confirmLabel: 'Suspend',
                                                type: 'warning',
                                                onConfirm: () => suspendPartnerUserMutation.mutate({ partnerId: company.id, userId: user.id }),
                                              })}
                                              disabled={suspendPartnerUserMutation.isPending}
                                              className="group/tip relative h-6 w-6 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded text-xs"
                                              aria-label="Suspend user"
                                            >
                                              <i className="fas fa-pause" />
                                              <ActionLabel text="Suspend user" />
                                            </button>
                                          )}
                                          <button
                                            onClick={() => confirm({
                                              title: 'Delete User',
                                              message: `Are you sure you want to permanently delete ${user.email}? This action cannot be undone.`,
                                              confirmLabel: 'Delete',
                                              type: 'danger',
                                              onConfirm: () => deletePartnerUserMutation.mutate({ partnerId: company.id, userId: user.id }),
                                            })}
                                            disabled={deletePartnerUserMutation.isPending}
                                            className="group/tip relative h-6 w-6 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded text-xs"
                                            aria-label="Delete user permanently"
                                          >
                                            <i className="fas fa-trash" />
                                            <ActionLabel text="Delete user permanently" />
                                          </button>
                                        </div>
                                      </div>
                                      {/* Permissions Editor Panel for this partner user */}
                                      {partnerPermissionsUser?.appUserId === user.appUserId && user.appUserId && (
                                        <div className="px-4 pb-3 pl-12">
                                          <div className="border border-purple-200 rounded-lg">
                                            <UserPermissionsEditor
                                              userId={user.appUserId}
                                              userName={user.fullName}
                                              userRole={user.role || 'Staff'}
                                              isPartner
                                              onClose={() => setPartnerPermissionsUser(null)}
                                              onRoleChanged={() => setPartnerPermissionsUser(null)}
                                            />
                                          </div>
                                        </div>
                                      )}
                                      {/* Client Access Editor Panel for this partner user */}
                                      {partnerClientAccessUser?.appUserId === user.appUserId && user.appUserId && (
                                        <div className="px-4 pb-3 pl-12">
                                          <div className="border border-blue-200 rounded-lg bg-white">
                                            <div className="px-3 py-2 border-b border-blue-100 bg-blue-50/50 flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <i className="fas fa-building text-blue-600 text-xs" />
                                                <span className="text-sm font-medium text-gray-900">Client Access</span>
                                                <span className="text-xs text-gray-500">for {partnerClientAccessUser?.name}</span>
                                              </div>
                                              <button
                                                onClick={() => {
                                                  setPartnerClientAccessUser(null);
                                                  setPartnerClientAccessPending([]);
                                                }}
                                                className="group/tip relative text-gray-400 hover:text-gray-600"
                                                aria-label="Close client access editor"
                                              >
                                                <i className="fas fa-times text-xs" />
                                                <ActionLabel text="Close client access editor" placement="bottom" />
                                              </button>
                                            </div>
                                            <div className="p-3">
                                              <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs text-gray-600">Select which clients this user can access:</span>
                                                <button
                                                  onClick={() => {
                                                    if (partnerClientAccessPending.length === clients.length) {
                                                      setPartnerClientAccessPending([]);
                                                    } else {
                                                      setPartnerClientAccessPending(clients.map(c => c.id));
                                                    }
                                                  }}
                                                  className="text-xs text-blue-600 hover:text-blue-700"
                                                >
                                                  {partnerClientAccessPending.length === clients.length ? 'Deselect All' : 'Select All'}
                                                </button>
                                              </div>
                                              <div className="grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto">
                                                {clients.map(client => (
                                                  <label key={client.id} className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-gray-50 cursor-pointer">
                                                    <input
                                                      type="checkbox"
                                                      checked={partnerClientAccessPending.includes(client.id)}
                                                      onChange={(e) => {
                                                        if (e.target.checked) {
                                                          setPartnerClientAccessPending([...partnerClientAccessPending, client.id]);
                                                        } else {
                                                          setPartnerClientAccessPending(partnerClientAccessPending.filter(id => id !== client.id));
                                                        }
                                                      }}
                                                      className="w-3.5 h-3.5 text-blue-600 rounded"
                                                    />
                                                    <span className="text-gray-800 truncate">{client.name}</span>
                                                  </label>
                                                ))}
                                              </div>
                                              <div className="mt-3 flex items-center justify-between">
                                                <span className="text-xs text-gray-500">
                                                  {partnerClientAccessPending.length} of {clients.length} clients selected
                                                </span>
                                                <div className="flex gap-2">
                                                  <button
                                                    onClick={() => {
                                                      setPartnerClientAccessUser(null);
                                                      setPartnerClientAccessPending([]);
                                                    }}
                                                    className="h-7 px-3 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded"
                                                  >
                                                    Cancel
                                                  </button>
                                                  <button
                                                    onClick={async () => {
                                                      if (partnerClientAccessPending.length === 0) {
                                                        toast.error('At least one client required', 'Staff users must have access to at least one client');
                                                        return;
                                                      }
                                                      try {
                                                        await updateUser.mutateAsync({
                                                          userId: partnerClientAccessUser!.appUserId,
                                                          updates: { clientIds: partnerClientAccessPending },
                                                        });
                                                        toast.success('Client access updated', `${partnerClientAccessUser!.name} now has access to ${partnerClientAccessPending.length} client(s)`);
                                                        // Update the specific user's clientIds in the cached partner users
                                                        const savedClientIds = [...partnerClientAccessPending];
                                                        const savedAppUserId = partnerClientAccessUser!.appUserId;
                                                        setCompanyUsers(prev => {
                                                          const updated: typeof prev = {};
                                                          for (const [companyId, users] of Object.entries(prev)) {
                                                            updated[companyId] = users.map(u =>
                                                              u.appUserId === savedAppUserId
                                                                ? { ...u, clientIds: savedClientIds }
                                                                : u
                                                            );
                                                          }
                                                          return updated;
                                                        });
                                                        setPartnerClientAccessUser(null);
                                                        setPartnerClientAccessPending([]);
                                                      } catch (error: any) {
                                                        toast.error('Failed to update', error?.response?.data?.error || 'Could not update client access');
                                                      }
                                                    }}
                                                    disabled={updateUser.isPending || partnerClientAccessPending.length === 0}
                                                    className="h-7 px-3 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                                                  >
                                                    {updateUser.isPending ? 'Saving...' : 'Save Changes'}
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      </React.Fragment>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ================================================================ */}
        {/* FOOTER */}
        {/* ================================================================ */}
        <div className="h-12 px-4 flex items-center justify-between border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <span className="text-sm text-gray-500">
            {activeDataset === 'internal' && `${filteredInternalPeople.length} ${internalFilter === 'all' ? 'people' : internalFilter === 'pre-invited' ? 'pre-invited' : 'user'}${filteredInternalPeople.length !== 1 ? (internalFilter === 'all' ? '' : 's') : ''}`}
            {activeDataset === 'external' && `${filteredCompanies.length} partner compan${filteredCompanies.length !== 1 ? 'ies' : 'y'}`}
          </span>
          <button
            onClick={onClose}
            className="h-8 px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
    </AnimatedModal>

    {/* Confirmation Dialog */}
    <ConfirmDialog {...dialogProps} />
    </>
  );
};

export default UserManagementModal;
